/**
 * DiscountListPage.js - 优惠码管理页
 * 支持新建、编辑、删除优惠码/折扣规则
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  DatePicker,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminDiscountApi, adminProductApi } from '../../api/index';

const { Title, Text } = Typography;
const { Option } = Select;

// 折扣类型标签
const typeConfig = {
  percentage: { label: '百分比折扣', suffix: '%' },
  fixed_amount: { label: '固定金额', prefix: '$' },
  free_shipping: { label: '免运费', suffix: '' },
};

export default function DiscountListPage() {
  const [discounts, setDiscounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Modal 状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [saving, setSaving] = useState(false);

  // 监听折扣类型变化（用于动态显示价值单位）
  const [discountType, setDiscountType] = useState('percentage');
  // 商品列表（用于商品折扣选择）
  const [products, setProducts] = useState([]);
  // 商品多选模式
  const [productSelectMode, setProductSelectMode] = useState(false);

  const [form] = Form.useForm();

  // 加载商品列表
  useEffect(() => {
    adminProductApi.list({ pageSize: 100 }).then((data) => {
      setProducts(data?.items || data || []);
    }).catch(() => {});
  }, []);

  // 拉取优惠码列表
  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminDiscountApi.list({ page, pageSize });
      if (Array.isArray(data)) {
        setDiscounts(data);
        setTotal(data.length);
      } else {
        setDiscounts(data?.list || data?.items || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      message.error('加载优惠码列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  // 打开新建 Modal
  const handleCreate = () => {
    setEditingDiscount(null);
    setDiscountType('percentage');
    form.resetFields();
    form.setFieldsValue({ type: 'percentage', enabled: true, isAuto: false });
    setModalVisible(true);
  };

  // 打开编辑 Modal
  const handleEdit = (record) => {
    setEditingDiscount(record);
    setDiscountType(record.type || 'percentage');
    const productIds = record.applyToProducts ? record.applyToProducts.split(',').map(id => parseInt(id)) : [];
    form.setFieldsValue({
      code: record.code,
      title: record.title,
      type: record.type,
      value: record.value,
      minPurchase: record.minPurchase,
      usageLimit: record.usageLimit,
      isAuto: record.isAuto || false,
      startsAt: record.startsAt ? dayjs(record.startsAt) : null,
      endsAt: record.endsAt ? dayjs(record.endsAt) : null,
      enabled: record.enabled !== false,
      applyToProducts: productIds,
      minQuantity: record.minQuantity,
    });
    setModalVisible(true);
  };

  // 关闭 Modal
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingDiscount(null);
    form.resetFields();
  };

  // 提交表单
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        ...values,
        value: values.type === 'percentage' ? Math.round(values.value) : values.value,
        minPurchase: values.minPurchase,
        startsAt: values.startsAt ? values.startsAt.toISOString() : null,
        endsAt: values.endsAt ? values.endsAt.toISOString() : null,
        // 商品 ID 数组转为逗号分隔字符串
        applyToProducts: values.applyToProducts && Array.isArray(values.applyToProducts) && values.applyToProducts.length > 0
          ? values.applyToProducts.join(',')
          : null,
        minQuantity: values.minQuantity || null,
      };

      if (editingDiscount) {
        await adminDiscountApi.update(editingDiscount.id, payload);
        message.success('优惠码已更新');
      } else {
        await adminDiscountApi.create(payload);
        message.success('优惠码已创建');
      }

      handleModalClose();
      fetchDiscounts();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 删除优惠码
  const handleDelete = async (id) => {
    try {
      await adminDiscountApi.delete(id);
      message.success('优惠码已删除');
      fetchDiscounts();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 格式化折扣值显示（后端已改为元为单位）
  const formatValue = (type, value) => {
    if (type === 'percentage') return `${value}%`;
    if (type === 'fixed_amount') return `$${(Number(value || 0)).toFixed(2)}`;
    return '-';
  };

  // 表格列配置
  const columns = [
    {
      title: '优惠码',
      dataIndex: 'code',
      key: 'code',
      render: (code, record) =>
        code ? (
          <Tag style={{ fontFamily: 'monospace', fontSize: 13 }}>{code}</Tag>
        ) : (
          <span style={{ color: '#aaa', fontSize: 13 }}>
            {record.isAuto ? '自动应用' : '-'}
          </span>
        ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (t) => (
        <span style={{ fontSize: 13 }}>{typeConfig[t]?.label || t}</span>
      ),
    },
    {
      title: '折扣值',
      key: 'value',
      render: (_, record) => formatValue(record.type, record.value),
    },
    {
      title: '适用商品',
      key: 'applyToProducts',
      ellipsis: true,
      render: (_, record) => {
        if (!record.applyToProducts) return <span style={{ color: '#aaa' }}>全部商品</span>;
        const count = record.applyToProducts.split(',').length;
        return <Tag color="blue">{count} 个商品</Tag>;
      },
    },
    {
      title: '最低数量',
      dataIndex: 'minQuantity',
      key: 'minQuantity',
      width: 90,
      render: (val) => val ? `${val}件` : '-',
    },
    {
      title: '最低消费',
      dataIndex: 'minPurchase',
      key: 'minPurchase',
      render: (val) =>
        val ? `$${(Number(val)).toFixed(2)}` : <span style={{ color: '#aaa' }}>无要求</span>,
    },
    {
      title: '使用次数',
      key: 'usage',
      render: (_, record) => (
        <span>
          {record.usageCount || 0}
          {record.usageLimit ? ` / ${record.usageLimit}` : ''}
        </span>
      ),
    },
    {
      title: '自动应用',
      dataIndex: 'isAuto',
      key: 'isAuto',
      width: 90,
      render: (val) => (val ? <Tag color="blue">自动</Tag> : '-'),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled) => (
        <Tag color={enabled !== false ? 'green' : 'default'}>
          {enabled !== false ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Popconfirm
          title="确认删除该优惠码？"
          onConfirm={() => handleDelete(record.id)}
          okText="删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="删除">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          优惠码管理
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建优惠码
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={discounts}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: '暂无优惠码' }}
          onRow={(record) => ({
            onClick: () => handleEdit(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* 新建/编辑 Modal */}
      <Modal
        title={editingDiscount ? '编辑优惠码' : '新建优惠码'}
        open={modalVisible}
        onCancel={handleModalClose}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 8 }}
          onValuesChange={(changed) => {
            if (changed.type) setDiscountType(changed.type);
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="code" label="优惠码（留空则为自动折扣）">
                <Input
                  placeholder="例：SAVE20"
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="title"
                label="标题"
                rules={[{ required: true, message: '请输入标题' }]}
              >
                <Input placeholder="折扣活动名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="折扣类型"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select>
                  <Option value="percentage">百分比折扣</Option>
                  <Option value="fixed_amount">固定金额</Option>
                  <Option value="free_shipping">免运费</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {discountType !== 'free_shipping' ? (
                <Form.Item
                  name="value"
                  label={
                    discountType === 'percentage'
                      ? '折扣百分比 (%)'
                      : '折扣金额 ($)'
                  }
                  rules={[{ required: true, message: '请输入折扣值' }]}
                >
                  <InputNumber
                    min={0}
                    max={discountType === 'percentage' ? 100 : undefined}
                    precision={discountType === 'percentage' ? 0 : 2}
                    suffix={discountType === 'percentage' ? '%' : undefined}
                    prefix={discountType === 'fixed_amount' ? '$' : undefined}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              ) : (
                <Form.Item label="折扣值">
                  <Input disabled value="免运费" />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="minPurchase" label="最低消费金额 ($)">
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="$"
                  placeholder="0.00（无要求则留空）"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="usageLimit" label="使用次数上限">
                <InputNumber
                  min={1}
                  placeholder="不限次数则留空"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="applyToProducts" label="适用商品">
                <Select
                  mode="multiple"
                  placeholder="不限商品（全部适用）"
                  allowClear
                  style={{ width: '100%' }}
                  maxTagCount="responsive"
                >
                  {products.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {products.length === 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  加载中...
                </Text>
              )}
            </Col>
            <Col span={12}>
              <Form.Item name="minQuantity" label="最低商品数量">
                <InputNumber
                  min={1}
                  placeholder="无要求则留空"
                  style={{ width: '100%' }}
                  suffix="件"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="startsAt" label="开始时间">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                  placeholder="立即生效"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endsAt" label="结束时间">
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: '100%' }}
                  placeholder="永久有效"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="isAuto"
                label="自动应用"
                valuePropName="checked"
                extra={<Text type="secondary" style={{ fontSize: 12 }}>开启后无需输入码自动触发</Text>}
              >
                <Switch checkedChildren="自动" unCheckedChildren="手动" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="enabled"
                label="启用状态"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
